<template>
  <div class="context-menu essay-popup">
    <v-dialog
      ref="contextDialog"
      v-model="isShown"
      :content-class="'essay-task-context-menu'"
    >
      <div class="essay-popup-header">
        <span class="essay-popup-header-dummy" />
        <span class="essay-popup-header-title">
          {{ $t('Essay.title.label') }}
        </span>
        <span class="essay-popup-header-close" @click="cancel()">
          <BaseSpriteIcon icon-name="ico-close" />
        </span>
      </div>
      <v-card>
        <div>
          <span class="control-label">{{ $t('Essay.topic.label') }}</span>
          <input
            v-model.trim="$v.currentEssay.topic.$model"
            :class="{ invalid: !topicValidation.isValid }"
            @input="onTopicInput"
          />
          <span class="counter-label">{{ topicSymbolsNumLabel }}</span>
        </div>
        <div>
          <span class="control-label">{{
            $t('Essay.writtenwords.label')
          }}</span>
          <input
            v-model.trim="$v.currentEssay.wordsLimit.$model"
            :class="{ invalid: !wordsLimitValidation.isValid }"
            @input="onWordsLimitInput"
          />
          <span class="counter-label">&nbsp;</span>
        </div>
        <div>
          <span class="control-label">{{ $t('Essay.comment.label') }}</span>
          <v-textarea
            v-model.trim="currentEssay.comment"
            class="comment-field"
            auto-grow
            :maxlength="MAX_CURRENT_ESSAY_LEN"
            @input="onCommentInput"
          />
          <span class="counter-label">{{ commentSymbolsNumLabel }}</span>
        </div>
      </v-card>
      <div class="essay-popup-footer">
        <div class="one-button" @click="saveEssay()">
          <v-btn>
            {{ $t('Save') }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('EssayTaskPopup.vue');

import { required, numeric } from 'vuelidate/lib/validators';
import MaterialsFactory from '@/classes/factories/Materials/MaterialsFactory';
import MaterialTypes from '@shared/enums/MaterialTypes';
import EssayTaskPopupEvents from '@/enums/EssayTaskPopupEvents';
import PopupEventEnum from '@/enums/PopupEventEnum';
import PopupNamesEnum from '@/enums/PopupNamesEnum';
import MenuStates from '@/enums/MenuStates';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon.vue';

const MAX_TOPIC_SYMBOLS_NUM = 100;
const MAX_COMMENT_SYMBOLS_NUM = 200;
const MAX_WORDS_LIMIT_SYMBOLS_NUM = 3;
const MAX_CURRENT_ESSAY_LEN = 200;
const DEFAULT_WORDS_LIMIT = 500;

const DEFAULT_ESSAY = {
  topic: '',
  wordsLimit: '' + DEFAULT_WORDS_LIMIT,
  comment: ''
};

const ADD_ESSAY_ACTION = 'MaterialsStore/addEssayTask';
const EDIT_ESSAY_ACTION = 'MaterialsStore/changeEssayTask';

export default {
  name: PopupNamesEnum.ESSAY_TASK_POPUP,
  components: { BaseSpriteIcon },
  props: {
    essayTaskPopupParams: Object
  },
  data() {
    return {
      MAX_CURRENT_ESSAY_LEN,
      isShown: true,
      currentEssay: { ...DEFAULT_ESSAY },
      topicValidation: {
        isValid: true
      },
      wordsLimitValidation: {
        isValid: true
      }
    };
  },
  validations: {
    currentEssay: {
      topic: {
        required
      },
      wordsLimit: {
        required,
        numeric
      }
    }
  },
  computed: {
    topicSymbolsNumLabel() {
      return `${this.currentEssay.topic.length}/${MAX_TOPIC_SYMBOLS_NUM}`;
    },
    commentSymbolsNumLabel() {
      return `${this.currentEssay.comment.length}/${MAX_COMMENT_SYMBOLS_NUM}`;
    }
  },
  watch: {
    isShown() {
      this.cancel();
    }
  },
  mounted() {
    if (this.essayTaskPopupParams.menuMode === MenuStates.EDIT) {
      this.currentEssay = { ...this.essayTaskPopupParams.essay };
    }
  },
  methods: {
    close() {
      this.isShown = false;
    },
    onTopicInput() {
      let topic = this.currentEssay.topic;
      this.currentEssay.topic = this.$_sliceIfExcess(
        topic,
        MAX_TOPIC_SYMBOLS_NUM
      );
      this.$_resetLazyValidation();
    },
    onWordsLimitInput() {
      let wordsLimit = this.currentEssay.wordsLimit;
      this.currentEssay.wordsLimit = this.$_sliceIfExcess(
        wordsLimit,
        MAX_WORDS_LIMIT_SYMBOLS_NUM
      );
      this.$_resetLazyValidation();
    },
    onCommentInput() {
      let comment = this.currentEssay.comment;
      this.currentEssay.comment = this.$_sliceIfExcess(
        comment,
        MAX_COMMENT_SYMBOLS_NUM
      );
      this.$_resetLazyValidation();
    },
    saveEssay() {
      this.$_resetLazyValidation();
      if (!this.$_isValidOnSave()) {
        return;
      }

      const essayTaskBuilder = MaterialsFactory.createBuilderByType(
        MaterialTypes.ESSAY_TASK
      );
      const essay = essayTaskBuilder
        .setId(this.currentEssay.id)
        .setCreatedAt(this.currentEssay.createdAt)
        .setWordsLimit(+this.currentEssay.wordsLimit)
        .setTopic(this.currentEssay.topic)
        .setComment(this.currentEssay.comment)
        .setPublicationId(this.essayTaskPopupParams.reqParams.bookId)
        .setParagraphId(this.essayTaskPopupParams.paragraphId)
        .build();

      const storeAction =
        this.essayTaskPopupParams.menuMode === MenuStates.ADD
          ? ADD_ESSAY_ACTION
          : EDIT_ESSAY_ACTION;

      this.$store.dispatch(storeAction, { essay });

      this.cancel();
    },
    cancel() {
      this.$emit('essayTaskPopupEvent', {
        type: PopupEventEnum.CLOSE
      });
    },
    $_isValidOnSave() {
      let isValid = true;
      if (this.$v.currentEssay.wordsLimit.$invalid) {
        this.wordsLimitValidation.isValid = false;
        isValid = false;
      }
      if (this.$v.currentEssay.topic.$invalid) {
        this.topicValidation.isValid = false;
        isValid = false;
      }
      return isValid;
    },
    $_essayTaskPopupErrorHandler(error) {
      logger.error(`Error in EssayTaskPopup: ${error}`);
      this.$emit('essayTaskPopupEvent', {
        type: EssayTaskPopupEvents.ERROR,
        data: { error }
      });
    },
    $_resetLazyValidation() {
      this.topicValidation.isValid = true;
      this.wordsLimitValidation.isValid = true;
    },
    $_sliceIfExcess(value, maxLength) {
      if (value.length > maxLength) {
        value = value.slice(0, maxLength);
      }
      return value;
    }
  }
};
</script>

<style lang="less" src="../EssayPopup/EssayPopup.less"></style>
<style lang="less" src="./EssayTaskPopup.less"></style>
